import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";

export type RingSegment = {
  /** Share of the ring, in percent. Segments at 0 are skipped. */
  percentage: number;
  color: string;
};

type Props = {
  segments: RingSegment[];
  /** Centre number (calories). */
  textInfo: string | number;
  unit?: string;
  size?: number;
  strokeWidth?: number;
  fontSize?: number;
  unitFontSize?: number;
};

/**
 * The app's `MultiPartCircularProgress`: a ring split into coloured segments
 * (carbs / protein / fat) starting at 12 o'clock, with the calories in the
 * centre. Plain SVG, same geometry as the react-native-svg original.
 */
const MultiPartCircularProgress = ({
  segments,
  textInfo,
  unit = "kcal",
  size = 120,
  strokeWidth = 7,
  fontSize = 16,
  unitFontSize = 14,
}: Props) => {
  const theme: Theme = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;

  let cumulativeAngle = -90;
  const arcs = segments
    .filter((segment) => segment.percentage > 0)
    .map((segment) => {
      const arcLength = (segment.percentage / 100) * circumference;
      const startAngle = cumulativeAngle;
      cumulativeAngle += (segment.percentage / 100) * 360;
      return { ...segment, arcLength, startAngle };
    });

  return (
    <Box sx={styles.container}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          stroke={theme.colors_ds.NEUTRAL_BG_ELEVATION1}
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        {arcs.map((arc, index) => (
          <circle
            key={index}
            stroke={arc.color}
            fill="none"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={`${arc.arcLength} ${
              circumference - arc.arcLength
            }`}
            strokeDashoffset={-arc.startAngle * (circumference / 360)}
            strokeLinecap="round"
          />
        ))}
        <text
          x={size / 2}
          y={size / 2 - 2}
          textAnchor="middle"
          fontSize={fontSize}
          fontWeight={500}
          fontFamily="var(--font-Medium)"
          fill={theme.colors_ds.NEUTRAL_TEXT_STRONG}
        >
          {textInfo}
        </text>
        <text
          x={size / 2}
          y={size / 2 + 18}
          textAnchor="middle"
          fontSize={unitFontSize}
          fontWeight={400}
          fontFamily="var(--font-Regular)"
          fill={theme.colors_ds.NEUTRAL_TEXT_DEFAULT}
        >
          {unit}
        </text>
      </svg>
    </Box>
  );
};

export default MultiPartCircularProgress;

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};
